import React, { useEffect, useState, useCallback, useMemo } from 'react';
import FailedAudits from './FailedAudits';
import { getParam, setParams } from '../urlState';

const API = 'https://tm-audit-platform-production.up.railway.app';

const STATUSES = ['new', 'reviewing', 'planned', 'fixed', 'released', 'wont_fix'];
const CATEGORIES = ['false_positive', 'missed_opportunity', 'parser_issue', 'confusing_report', 'wrong_dollar_amount', 'feature_request', 'other'];
const SEVERITIES = ['critical', 'high', 'medium', 'low'];

const labelize = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
const fmtMoney = (n) => (n === null || n === undefined) ? '—' : '$' + Number(n).toLocaleString();
const fmtDate = (d) => d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';
const fmtDateShort = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—';

const sevColor = (s) => s === 'critical' ? 'var(--red)' : s === 'high' ? 'var(--amber)' : s === 'medium' ? 'var(--blue)' : 'var(--text-muted)';
const statusColor = (s) => s === 'new' ? 'var(--amber)' : s === 'fixed' || s === 'released' ? 'var(--green)' : s === 'wont_fix' ? 'var(--text-muted)' : 'var(--blue)';

// Pick a single calm icon + accent color per row. Wins read green, ideas amber,
// parser/internal blue, everything else (issues) red. Keeps the table scannable
// without a wall of badges.
const rowIcon = (r) => {
    if (r.feedback_type === 'positive') return { glyph: '✓', color: 'var(--green)', bg: 'var(--green-dim)' };
    if (r.category === 'feature_request') return { glyph: '✦', color: 'var(--amber-bright)', bg: 'var(--amber-dim)' };
    if (r.category === 'parser_issue') return { glyph: '⟨⟩', color: 'var(--blue)', bg: 'var(--blue-dim)' };
    return { glyph: '!', color: 'var(--red)', bg: 'var(--red-dim)' };
};

// Short human title for the Feedback cell. Prefer the customer's own note, then
// fall back to a friendly category label (or "Customer Win" for positives).
const rowTitle = (r) => {
    const note = (r.notes || '').trim();
    if (note) return note;
    if (r.feedback_type === 'positive') return 'Customer Win';
    return r.category ? labelize(r.category) : 'Feedback';
};

// Admin-only Feedback Center: the internal surface for "a customer hit something
// and a person needs to respond." Two sub-views over two tables —
//
//   Customer Feedback : what they told us      (audit_feedback, migration 0019/0020)
//   Failed Audits     : what never worked      (audit_failures, migration 0031/0032)
//
// A sub-view rather than a second admin tab: failed audits are the same triage job
// against a different table, and the dashboard does not need another top-level
// entry for it. Never exposed to normal users — only reachable from AdminDashboard.
const SUBVIEWS = ['feedback', 'failures'];

function FeedbackCenter({ session }) {
    // Survives a hard refresh per the repo's URL-state standard (see urlState.js).
    // Validated against the allowlist — the URL is user input, and an unknown value
    // falls back to the default rather than rendering nothing.
    const [subview, setSubview] = useState(() => {
        const s = getParam('sub');
        return SUBVIEWS.includes(s) ? s : 'feedback';
    });

    const go = (s) => { setSubview(s); setParams({ sub: s === 'feedback' ? null : s }); };

    return (
        <div>
            <div className="feedback-subnav">
                <button className={`feedback-hbtn ${subview === 'feedback' ? 'active' : ''}`} onClick={() => go('feedback')}>
                    Customer Feedback
                </button>
                <button className={`feedback-hbtn ${subview === 'failures' ? 'active' : ''}`} onClick={() => go('failures')}>
                    Failed Audits
                </button>
            </div>
            {subview === 'feedback' ? <CustomerFeedback session={session} /> : <FailedAudits session={session} />}
        </div>
    );
}

// The original Feedback Center view, unchanged apart from its name — it is now one
// of two sub-views rather than the whole tab.
function CustomerFeedback({ session }) {
    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({ status: '', category: '', severity: '', feedback_type: '', needs_corpus_review: '', reference_library_candidate: '' });
    const [search, setSearch] = useState('');
    const [showFilters, setShowFilters] = useState(true);
    const [showMore, setShowMore] = useState(false);
    const [selected, setSelected] = useState(null);
    const [saving, setSaving] = useState(false);

    const getHeaders = useCallback(() => ({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.access_token || ''}`,
    }), [session]);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const qs = new URLSearchParams();
            Object.entries(filters).forEach(([k, v]) => { if (v) qs.set(k, v); });
            const res = await fetch(`${API}/api/admin/feedback?${qs.toString()}`, { headers: getHeaders() });
            if (!res.ok) throw new Error(`Request failed (${res.status})`);
            const data = await res.json();
            setRows(data.feedback || []);
            setSummary(data.summary || null);
        } catch (e) {
            setError(e.message);
        }
        setLoading(false);
    }, [filters, getHeaders]);

    useEffect(() => { load(); }, [load]);

    const patchSelected = async (patch) => {
        if (!selected) return;
        setSaving(true);
        try {
            const res = await fetch(`${API}/api/admin/feedback/${selected.id}`, {
                method: 'PATCH',
                headers: getHeaders(),
                body: JSON.stringify(patch),
            });
            if (!res.ok) throw new Error(`Update failed (${res.status})`);
            const data = await res.json();
            setSelected(data.feedback);
            setRows(prev => prev.map(r => r.id === data.feedback.id ? data.feedback : r));
        } catch (e) {
            setError(e.message);
        }
        setSaving(false);
    };

    const setFilter = (key, value) => setFilters(prev => ({ ...prev, [key]: prev[key] === value ? '' : value }));
    const setSelect = (key, value) => setFilters(prev => ({ ...prev, [key]: value }));

    // Client-side search across the loaded rows — no backend round-trip needed.
    const visibleRows = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter(r => [r.notes, r.user_email, r.job_name, r.file_name, r.category, r.status]
            .some(v => (v || '').toString().toLowerCase().includes(q)));
    }, [rows, search]);

    const cards = summary ? [
        { key: 'new', label: 'New', value: summary.new, desc: 'Needs your attention', color: 'var(--amber)' },
        { key: 'critical', label: 'Critical', value: summary.critical_high, desc: 'High priority issues', color: 'var(--red)' },
        { key: 'ideas', label: 'Ideas', value: summary.feature_requests, desc: 'Feature requests', color: 'var(--amber-bright)' },
        { key: 'parser', label: 'Parser', value: summary.parser_issues, desc: 'Parsing-related issues', color: 'var(--blue)' },
        { key: 'wins', label: 'Wins', value: summary.wins, desc: 'Customer wins', color: 'var(--green)' },
    ] : [];

    return (
        <div className="feedback-center">
            {/* Page header */}
            <div className="feedback-header">
                <div className="feedback-header-text">
                    <h2 className="feedback-title">Feedback</h2>
                    <p className="feedback-subtitle">See what customers are saying and decide what Audee should improve next.</p>
                </div>
                <div className="feedback-header-actions">
                    <button className={`feedback-hbtn ${showFilters ? 'active' : ''}`} onClick={() => setShowFilters(s => !s)}>Filters</button>
                    <button className="feedback-hbtn" onClick={load}>↻ Refresh</button>
                </div>
            </div>

            {/* Summary cards */}
            <div className="feedback-cards">
                {cards.map(c => (
                    <div className="feedback-card" key={c.key}>
                        <div className="feedback-card-value" style={{ color: c.color }}>{c.value}</div>
                        <div className="feedback-card-label">{c.label}</div>
                        <div className="feedback-card-desc">{c.desc}</div>
                    </div>
                ))}
            </div>

            {/* Compact controls */}
            {showFilters && (
                <div className="feedback-controls-wrap">
                    <div className="feedback-controls">
                        <Select value={filters.feedback_type} onChange={v => setSelect('feedback_type', v)} placeholder="All Types"
                            options={[{ v: 'issue', l: 'Issue' }, { v: 'positive', l: 'Win' }]} />
                        <Select value={filters.status} onChange={v => setSelect('status', v)} placeholder="All Statuses"
                            options={STATUSES.map(s => ({ v: s, l: labelize(s) }))} />
                        <Select value={filters.category} onChange={v => setSelect('category', v)} placeholder="All Categories"
                            options={CATEGORIES.map(s => ({ v: s, l: labelize(s) }))} />
                        <Select value={filters.severity} onChange={v => setSelect('severity', v)} placeholder="All Severities"
                            options={SEVERITIES.map(s => ({ v: s, l: labelize(s) }))} />
                        <input className="feedback-search" type="text" placeholder="Search feedback…" value={search} onChange={e => setSearch(e.target.value)} />
                        <button className={`feedback-hbtn ${showMore ? 'active' : ''}`} onClick={() => setShowMore(s => !s)}>
                            {showMore ? 'Less Filters' : 'More Filters'}
                        </button>
                    </div>

                    {showMore && (
                        <div className="feedback-more">
                            <span className="feedback-more-label">Review</span>
                            <button className={`feedback-toggle ${filters.needs_corpus_review === 'true' ? 'active' : ''}`} onClick={() => setFilter('needs_corpus_review', 'true')}>Needs Review</button>
                            <button className={`feedback-toggle ${filters.reference_library_candidate === 'true' ? 'active' : ''}`} onClick={() => setFilter('reference_library_candidate', 'true')}>Library Candidate</button>
                        </div>
                    )}
                </div>
            )}

            {error && <div className="feedback-error" style={{ margin: '8px 0' }}>{error}</div>}
            {loading && <div className="history-loading"><div className="spinner" /></div>}

            {!loading && visibleRows.length === 0 && (
                <div className="feedback-empty">
                    {rows.length === 0
                        ? `No feedback yet${Object.values(filters).some(Boolean) ? ' for these filters' : ''}.`
                        : 'No feedback matches your search.'}
                </div>
            )}

            {!loading && visibleRows.length > 0 && (
                <div className="admin-table-wrap">
                    <table className="admin-table feedback-table">
                        <thead>
                            <tr>
                                <th>Feedback</th>
                                <th>Type</th>
                                <th>Severity</th>
                                <th>Status</th>
                                <th>Job / File</th>
                                <th>Recoverable</th>
                                <th>Date</th>
                                <th aria-label="Open" />
                            </tr>
                        </thead>
                        <tbody>
                            {visibleRows.map(r => {
                                const icon = rowIcon(r);
                                return (
                                    <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => setSelected(r)}>
                                        <td>
                                            <div className="feedback-cell">
                                                <span className="feedback-cell-icon" style={{ color: icon.color, background: icon.bg }}>{icon.glyph}</span>
                                                <div className="feedback-cell-main">
                                                    <div className="feedback-cell-title">
                                                        {rowTitle(r)}
                                                        {r.needs_corpus_review && <span className="feedback-badge feedback-badge-blue">Needs Review</span>}
                                                        {r.reference_library_candidate && <span className="feedback-badge feedback-badge-amber">Library</span>}
                                                    </div>
                                                    <div className="feedback-cell-sub">{r.user_email || 'Anonymous'}{r.plan ? ` · ${r.plan}` : ''}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <span style={{ color: r.feedback_type === 'positive' ? 'var(--green)' : 'var(--amber)', fontFamily: 'var(--mono)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>
                                                {r.feedback_type === 'positive' ? 'Win' : 'Issue'}
                                            </span>
                                        </td>
                                        <td style={{ color: sevColor(r.severity), fontFamily: 'var(--mono)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>{r.severity || '—'}</td>
                                        <td style={{ color: statusColor(r.status), fontFamily: 'var(--mono)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>{labelize(r.status)}</td>
                                        <td style={{ fontSize: 12 }}>{r.job_name || r.file_name || '—'}</td>
                                        <td style={{ color: r.recoverable_total > 0 ? 'var(--green)' : undefined, fontFamily: 'var(--mono)', fontWeight: r.recoverable_total > 0 ? 700 : undefined }}>{fmtMoney(r.recoverable_total)}</td>
                                        <td style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{fmtDateShort(r.created_at)}</td>
                                        <td className="feedback-chevron">›</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {selected && (
                <FeedbackDetail
                    item={selected}
                    saving={saving}
                    onClose={() => setSelected(null)}
                    onPatch={patchSelected}
                />
            )}
        </div>
    );
}

// Lightweight native-select wrapper styled to match the dark theme. An empty
// value renders the placeholder ("All …") so it doubles as the clear option.
function Select({ value, onChange, options, placeholder }) {
    return (
        <select className="feedback-control-select" value={value} onChange={e => onChange(e.target.value)}>
            <option value="">{placeholder}</option>
            {options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
        </select>
    );
}

function FeedbackDetail({ item, saving, onClose, onPatch }) {
    const [internalNotes, setInternalNotes] = useState(item.internal_notes || '');
    const [fixedIn, setFixedIn] = useState(item.fixed_in || '');
    const [relRelease, setRelRelease] = useState(item.related_release || '');
    const [relCommit, setRelCommit] = useState(item.related_commit || '');

    // Re-sync the editable fields when a different row is opened or after a save
    // returns the canonical server row.
    useEffect(() => {
        setInternalNotes(item.internal_notes || '');
        setFixedIn(item.fixed_in || '');
        setRelRelease(item.related_release || '');
        setRelCommit(item.related_commit || '');
    }, [item.id, item.internal_notes, item.fixed_in, item.related_release, item.related_commit]);

    const Row = ({ label, value }) => (
        <div className="feedback-detail-row">
            <span className="feedback-detail-key">{label}</span>
            <span className="feedback-detail-val">{value === null || value === undefined || value === '' ? '—' : value}</span>
        </div>
    );

    const ids = (arr) => Array.isArray(arr) && arr.length ? arr.join(', ') : '—';

    return (
        <div className="gd-drawer-overlay" onClick={onClose}>
            <div className="gd-drawer" onClick={e => e.stopPropagation()}>
                <div className="gd-drawer-head">
                    <div className="gd-drawer-title">
                        {item.feedback_type === 'positive' ? 'Customer Win' : 'Issue'}
                        {item.category ? ` · ${labelize(item.category)}` : ''}
                    </div>
                    <button className="history-back-link" onClick={onClose}>✕ Close</button>
                </div>

                {item.notes && (
                    <div className="feedback-detail-notes">{item.notes}</div>
                )}

                {/* Triage controls */}
                <div className="feedback-detail-controls">
                    <label className="feedback-field">
                        <span className="feedback-field-label">Status</span>
                        <select className="feedback-select" value={item.status} disabled={saving} onChange={e => onPatch({ status: e.target.value })}>
                            {STATUSES.map(s => <option key={s} value={s}>{labelize(s)}</option>)}
                        </select>
                    </label>
                    {item.feedback_type !== 'positive' && (
                        <label className="feedback-field">
                            <span className="feedback-field-label">Severity</span>
                            <select className="feedback-select" value={item.severity || ''} disabled={saving} onChange={e => onPatch({ severity: e.target.value || null })}>
                                <option value="">—</option>
                                {SEVERITIES.map(s => <option key={s} value={s}>{labelize(s)}</option>)}
                            </select>
                        </label>
                    )}
                </div>

                <label className="feedback-field" style={{ marginTop: 12 }}>
                    <span className="feedback-field-label">Internal notes</span>
                    <textarea className="feedback-textarea" rows={3} value={internalNotes} disabled={saving} onChange={e => setInternalNotes(e.target.value)} onBlur={() => { if (internalNotes !== (item.internal_notes || '')) onPatch({ internal_notes: internalNotes }); }} />
                </label>

                <label className="feedback-field">
                    <span className="feedback-field-label">Fixed in (release)</span>
                    <input className="feedback-select" type="text" placeholder="e.g. RC2 / d189dcb" value={fixedIn} disabled={saving} onChange={e => setFixedIn(e.target.value)} onBlur={() => { if (fixedIn !== (item.fixed_in || '')) onPatch({ fixed_in: fixedIn }); }} />
                </label>

                {/* Review triage */}
                <div className="feedback-detail-section">Review</div>
                <div className="feedback-detail-controls">
                    <button
                        className={`feedback-toggle ${item.needs_corpus_review ? 'active' : ''}`}
                        disabled={saving}
                        onClick={() => onPatch({ needs_corpus_review: !item.needs_corpus_review })}
                    >
                        {item.needs_corpus_review ? '✓ ' : ''}Needs Review
                    </button>
                    <button
                        className={`feedback-toggle ${item.reference_library_candidate ? 'active' : ''}`}
                        disabled={saving}
                        onClick={() => onPatch({ reference_library_candidate: !item.reference_library_candidate })}
                    >
                        {item.reference_library_candidate ? '✓ ' : ''}Library Candidate
                    </button>
                </div>
                <div className="feedback-detail-controls" style={{ marginTop: 12 }}>
                    <label className="feedback-field">
                        <span className="feedback-field-label">Related release</span>
                        <input className="feedback-select" type="text" placeholder="e.g. RC2" value={relRelease} disabled={saving} onChange={e => setRelRelease(e.target.value)} onBlur={() => { if (relRelease !== (item.related_release || '')) onPatch({ related_release: relRelease }); }} />
                    </label>
                    <label className="feedback-field">
                        <span className="feedback-field-label">Related commit</span>
                        <input className="feedback-select" type="text" placeholder="e.g. d189dcb" value={relCommit} disabled={saving} onChange={e => setRelCommit(e.target.value)} onBlur={() => { if (relCommit !== (item.related_commit || '')) onPatch({ related_commit: relCommit }); }} />
                    </label>
                </div>

                {/* Captured context */}
                <div className="feedback-detail-section">Captured context</div>
                <Row label="Feedback ID" value={item.id} />
                <Row label="Submitted" value={fmtDate(item.created_at)} />
                <Row label="User" value={item.user_email || 'Anonymous'} />
                <Row label="Plan" value={item.plan} />
                <Row label="Reviewed By" value={item.reviewed_by} />
                <Row label="Document Type" value={item.document_type} />
                <Row label="File" value={item.file_name} />
                <Row label="Job" value={item.job_name} />
                <Row label="Audit ID" value={item.audit_id} />
                <Row label="Audit History ID" value={item.audit_history_id} />
                <Row label="Estimate Total" value={fmtMoney(item.estimate_total)} />
                <Row label="Recoverable Total" value={fmtMoney(item.recoverable_total)} />
                <Row label="Requires Correction" value={item.requires_correction_count} />
                <Row label="Opportunities" value={item.opportunity_count} />
                <Row label="Prepare To Explain" value={item.prepare_to_explain_count} />
                <Row label="Rule IDs" value={ids(item.rule_ids)} />
                <Row label="Finding IDs" value={ids(item.finding_ids)} />
                <Row label="App Version" value={item.app_version} />
                <Row label="Related Release" value={item.related_release} />
                <Row label="Related Commit" value={item.related_commit} />
                <Row label="PDF Downloaded" value={item.pdf_downloaded ? 'Yes' : 'No'} />
                <Row label="Time On Report" value={item.time_on_report_seconds != null ? `${item.time_on_report_seconds}s` : null} />
                <Row label="Browser Size" value={(item.browser_width && item.browser_height) ? `${item.browser_width} × ${item.browser_height}` : null} />
                <Row label="Details Expanded" value={item.details_expanded ? 'Yes' : 'No'} />
                <Row label="Sections Opened" value={ids(item.sections_expanded)} />
                <Row label="Current URL" value={item.current_url} />
                <Row label="Browser" value={item.browser_user_agent} />
            </div>
        </div>
    );
}

export default FeedbackCenter;
